import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ModalCrearTurno } from '../../pages/Recepcion/TurneroPage';
import { obtenerCitacionDeEvento } from '../../services/turneroService';
import turneroStyles from '../../style/pages/Turnero.module.css';

/** "11:00" → "10:00": la citación al estudio se sugiere una hora antes de la junta o pericia. */
export function unaHoraAntes(hora) {
    const [h, m] = String(hora || '').slice(0, 5).split(':').map(Number);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return '';
    const total = Math.max(0, h * 60 + m - 60);
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * El turno del turnero que cita al cliente en el estudio antes de la junta o pericia, ya vinculado
 * al evento (V254). Fecha la del evento, hora una antes (editables) y tipo JUNTA_MEDICA o PERICIA,
 * que no cuentan en las estadísticas de Recepción.
 */
export function ModalCitacionEstudio({ evento, tipo, cliente, onCerrar, onCreado }) {
    if (!evento) return null;
    return (
        <div className={turneroStyles.overlay} onClick={onCerrar}>
            <div className={turneroStyles.contenidoModal} onClick={(e) => e.stopPropagation()}>
                <ModalCrearTurno
                    celdaSel={null}
                    fechaSugerida={String(evento.fecha || '').slice(0, 10)}
                    horaSugerida={unaHoraAntes(evento.horaInicio || evento.hora)}
                    clienteInicial={cliente}
                    idEventoCitado={evento.idEvento}
                    valoresIniciales={{
                        tipoTramite: tipo,
                        // Ya es cliente del estudio (tiene la carpeta abierta): no es su primera vez.
                        tieneCarpeta: true,
                        esPrimeraVez: 'NO',
                    }}
                    onCerrar={onCerrar}
                    onCreado={() => {
                        toast.success('Cliente citado en el estudio. El recordatorio de WhatsApp va a usar esta hora.');
                        onCreado?.();
                    }}
                />
            </div>
        </div>
    );
}

/**
 * Debajo de la junta médica o pericia: si el cliente ya está citado en el estudio (y a qué hora), o
 * el botón para citarlo. El recordatorio de WhatsApp del evento le dice las dos horas cuando hay
 * citación; sin citación, solo la de la junta/pericia.
 *
 * @param tipo        JUNTA_MEDICA | PERICIA (el tipo del turno en el turnero)
 * @param abrirAlMontar abre el modal de entrada (recién registrada la junta)
 */
export default function TarjetaCitacion({ evento, tipo, cliente, abrirAlMontar = false, onAbierto }) {
    const [citacion, setCitacion] = useState(null);
    const [cargando, setCargando] = useState(true);
    const [abierto, setAbierto] = useState(false);
    const idEvento = evento?.idEvento;

    const cargar = useCallback(async () => {
        if (!idEvento) return;
        setCargando(true);
        try {
            setCitacion(await obtenerCitacionDeEvento(idEvento));
        } catch {
            setCitacion(null);
        } finally {
            setCargando(false);
        }
    }, [idEvento]);

    useEffect(() => { cargar(); }, [cargar]);

    useEffect(() => {
        if (abrirAlMontar && idEvento) {
            setAbierto(true);
            onAbierto?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [abrirAlMontar, idEvento]);

    if (!idEvento) return null;
    const nombreEvento = tipo === 'PERICIA' ? 'la pericia' : 'la junta';

    return (
        <div className="mt-3 p-3 rounded-3" style={{
            border: `1px solid ${citacion ? '#bbf7d0' : '#fde68a'}`,
            background: citacion ? '#f0fdf4' : '#fffbeb',
        }}>
            {cargando ? (
                <span style={{ fontSize: '0.85rem', color: '#4b5563' }}>Buscando la citación…</span>
            ) : citacion ? (
                <div style={{ fontSize: '0.9rem', color: '#14532d' }}>
                    <i className="bi bi-calendar-check me-2" />
                    Citado en el estudio el <strong>{citacion.fechaTurno?.split('-').reverse().join('/')}</strong> a las{' '}
                    <strong>{citacion.horario}</strong>. El recordatorio de WhatsApp le dice esta hora y la de {nombreEvento}.
                    <div style={{ fontSize: '0.8rem', color: '#4b5563' }}>Para cambiarla, mové o cancelá el turno en el turnero.</div>
                </div>
            ) : (
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                    <span style={{ fontSize: '0.9rem', color: '#78350f' }}>
                        <i className="bi bi-exclamation-circle me-2" />
                        Sin citación en el estudio: el recordatorio le va a decir solo la hora de {nombreEvento}.
                    </span>
                    <button type="button" className="btn btn-sm btn-warning" onClick={() => setAbierto(true)}>
                        <i className="bi bi-calendar-plus me-1" />Citar en el estudio
                    </button>
                </div>
            )}
            {abierto && (
                <ModalCitacionEstudio
                    evento={evento}
                    tipo={tipo}
                    cliente={cliente}
                    onCerrar={() => setAbierto(false)}
                    onCreado={() => { setAbierto(false); cargar(); }}
                />
            )}
        </div>
    );
}
